import { FAQPageJsonLd } from "next-seo";
import Link from "next/link";
import { ReactNode } from "react";

import { AppLayout } from "shared/Layout";
import { QuestionDropdown } from "shared/QuestionDropDown";
import { fetchSettings } from "shared/SettingsProvider/settings.helper";
import { Button } from "ui/Button";
import { CACHE_ONE_WEEK } from "utils/config.utils";
import { fetchPages } from "utils/fetch-page.utilts";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { fetchSEO, withSeo } from "utils/with-seo.utils";

export interface Question {
  id: string;
  question: string;
  answer: string;
  analyticsKey: string;
}

type FAQPageProps = { questions: Question[] };

const FAQPage = ({ questions }: FAQPageProps) => {
  const i18n = useI18n();

  return (
    <>
      <FAQPageJsonLd
        mainEntity={questions.map(
          ({ question: questionName, answer: acceptedAnswerText }) => ({
            questionName,
            acceptedAnswerText,
          }),
        )}
      />
      <section className="flex flex-col py-28 m-auto max-w-4xl">
        <h1 className="mb-3 text-4xl font-medium text-center lg:mb-4 lg:text-6xl">
          {i18n.t("faq.title")}
        </h1>
        {questions.map(({ id, question, answer, analyticsKey }) => (
          <QuestionDropdown
            key={id}
            question={question}
            answer={answer}
            analyticsKey={analyticsKey}
          />
        ))}
        <h2 className="px-4 my-4 text-3xl font-medium text-center lg:my-12 lg:text-6xl">
          {i18n.t("faq.footer")}
        </h2>
        <Link href="https://aide.mobile.club/">
          <a className="m-auto w-fit" target="_blank">
            <Button>{i18n.t("faq.cta")}</Button>
          </a>
        </Link>
      </section>
    </>
  );
};

FAQPage.getLayout = (page: ReactNode, pageProps: any) => (
  <AppLayout {...pageProps}>{page}</AppLayout>
);

export async function getStaticProps() {
  const [questions] = await fetchPages<Question[][]>("faqs");
  const translations = await fetchTranslations("faq");

  const seo = await fetchSEO("faq");

  return {
    props: { translations, questions, seo, settings: await fetchSettings() },
    revalidate: CACHE_ONE_WEEK,
  };
}

export default withApp(FAQPage, [withSeo]);
